import difference from 'lodash/difference';
import flatMap from 'lodash/flatMap';
import memoize from 'lodash/memoize';

import { OptionBindingPurchasabilityValidationError } from './components/OfferHeader/OfferTags';
import { UpdateOptionPayload } from './components/OptionForm';
import { Offer, Option, OptionBinding } from './types';

export const getOfferPurchasabilityErrors = (options: Option[], offer: Offer) =>
  validateOfferPurchasability(options, offer).map(validationError => {
    const optionBinding = offer.option_bindings?.find(ob => ob.id === validationError.optionBindingId);
    if (!optionBinding) {
      throw new Error(`No option binding found with id ${validationError.optionBindingId}`);
    }
    const option = options.find(o => o.id === optionBinding.option_id);
    if (!option) {
      throw new Error(`No option found with id ${optionBinding.option_id}`);
    }
    return {
      optionName: option.name,
      error: validationError.error,
    };
  });

const buildOptionIndexById = memoize((options: Option[]) => {
  const optionByOptionId: { [optionId: string]: Option } = {};
  for (const option of options) {
    optionByOptionId[option.id] = option;
  }
  return optionByOptionId;
});

// Use WeakMap to avoid leaking memory. Overriding the cache of a memoized function instance is not documented, but it
// works with current Lodash memoize implementation. It is possible that in future this override breaks and memoize will
// resort to default cache implementation which leaks memory.
buildOptionIndexById.cache = new WeakMap();

export const validateOfferPurchasability = (
  options: Option[],
  offer: Offer,
): { optionBindingId: string; error: OptionBindingPurchasabilityValidationError }[] => {
  const optionIndex = buildOptionIndexById(options);
  const optionsBoundToOffer =
    offer.option_bindings?.map(ob => {
      const option: Option | undefined = optionIndex[ob.option_id];
      if (!option) {
        throw new Error(`Option with ID ${ob.option_id} not found`);
      }
      return option;
    }) ?? [];

  return flatMap(offer.option_bindings ?? [], optionBinding => {
    const validationErrors = validateOptionBinding(optionsBoundToOffer, optionBinding);
    return validationErrors.map(validationError => ({
      optionBindingId: optionBinding.id,
      error: validationError,
    }));
  });
};

export const getUnpurchasableOffers = (offers: Offer[], options: Option[]): Offer[] => {
  return offers.filter(offer => validateOfferPurchasability(options, offer).length > 0);
};

const validateOptionBinding = (
  optionsBoundToOffer: Option[],
  optionBinding: OptionBinding,
): OptionBindingPurchasabilityValidationError[] => {
  if (!optionBindingCanBeVisible(optionsBoundToOffer, optionBinding)) {
    return [];
  }

  const option = optionsBoundToOffer.find(o => o.id === optionBinding.option_id);
  if (!option) {
    throw new Error(`Option with ID ${optionBinding.option_id} not found`);
  }

  if (option.type === 'multi_choice') {
    const multiChoiceConfig = optionBinding.multi_choice_config;
    if (!multiChoiceConfig) {
      return ['binding_missing_multi_choice_config'];
    }

    const numAvailableSelections = option.values.reduce((acc, optionChoice) => {
      if (!optionChoice.multi_choice_config) {
        throw new Error(`Multi-choice option value with ID ${optionChoice.id} has no multi_choice_config`);
      }

      return optionChoice.enabled.enabled
        ? acc + (optionChoice.multi_choice_config.selection_range.max || multiChoiceConfig.max_single_selections)
        : acc;
    }, 0);
    return numAvailableSelections >= multiChoiceConfig.total_selections.min ? [] : ['too_many_disabled_option_values'];
  }

  return option.values.some(value => value.enabled.enabled) ? [] : ['all_option_values_disabled'];
};

const optionBindingCanBeVisible = (optionsBoundToOffer: Option[], optionBinding: OptionBinding) => {
  return (optionBinding.prerequisite_values ?? []).every(prerequisiteChoiceId => {
    return (
      optionChoiceBelongsToOptions(optionsBoundToOffer, prerequisiteChoiceId) &&
      isOptionChoiceEnabled(optionsBoundToOffer, prerequisiteChoiceId)
    );
  });
};

const isOptionChoiceEnabled = (options: Option[], optionChoiceId: string) => {
  const allOptionChoices = flatMap(options, option => option.values);
  const prerequisiteChoice = allOptionChoices.find(optionChoice => optionChoice.id === optionChoiceId);
  if (!prerequisiteChoice) {
    throw new Error(`Option value with ID ${optionChoiceId} not found`);
  }
  return prerequisiteChoice.enabled.enabled;
};

const optionChoiceBelongsToOptions = (options: Option[], optionChoiceId: string) => {
  return options.some(o => o.values.some(ov => ov.id === optionChoiceId));
};

export const getOffersThatWillBecomeUnpurchasable = (
  simulatedOption: Option,
  allOffers: Offer[],
  allOptions: Option[],
) => {
  const currentUnpurchasableOffers = getUnpurchasableOffers(allOffers, allOptions);
  const nextUnpurchasableOffers = getUnpurchasableOffers(
    allOffers,
    allOptions.map(o => (o.id === simulatedOption.id ? simulatedOption : o)),
  );
  return difference(nextUnpurchasableOffers, currentUnpurchasableOffers);
};

export const simulateOptionUpdateApiCall = (originalOption: Option, payload: UpdateOptionPayload): Option => {
  return {
    default_value_index: payload.default_value_index,
    external_id: payload.external_id,
    id: originalOption.id,
    name: payload.name,
    scope: originalOption.scope,
    type: payload.type,
    values: payload.values.map((value, index) => ({
      ...value,
      id: value.id || `new-value-${index}`,
    })),
    version: originalOption.version,
  };
};
